import {createSlice, PayloadAction} from '@reduxjs/toolkit';
import {VALIDSIGN_SUPPORT_API_URL, DEFAULT_SUPPORT_LOCALE} from './infoArticleConstants';
import {Article, InfoArticleState} from './types';
import httpClient from '../../../utils/httpClient';

interface InfoArticleRootState {
    infoArticle: InfoArticleState;
    app: { locale: string };
}

const initialState: InfoArticleState = {
    infoArticle: null,
    isFetchingInfoArticle: false,
    fetchInfoArticleError: null,
    selectedInfoArticleId: null,
    supportLocale: DEFAULT_SUPPORT_LOCALE
};

export const infoArticleSlice = createSlice({
    name: 'infoArticle',
    initialState,
    reducers: {
        setInfoArticle: (state, action: PayloadAction<Article | null>) => {
            state.infoArticle = action.payload;
        },
        setIsFetchingInfoArticle: (state, action: PayloadAction<boolean>) => {
            state.isFetchingInfoArticle = action.payload;
        },
        setFetchInfoArticleError: (state, action: PayloadAction<string | null>) => {
            state.fetchInfoArticleError = action.payload;
        },
        setSelectedInfoArticleId: (state, action: PayloadAction<string | null>) => {
            state.selectedInfoArticleId = action.payload;
        },
        setSupportLocale: (state, action: PayloadAction<string>) => {
            state.supportLocale = action.payload;
        }
    }
});

export const {
    setInfoArticle,
    setIsFetchingInfoArticle,
    setFetchInfoArticleError,
    setSelectedInfoArticleId,
    setSupportLocale
} = infoArticleSlice.actions;

export const initializeSupportLocale = () => async (dispatch: any, getState: () => InfoArticleRootState) => {
    const {locale} = getState().app;
    let localeSupport = locale;
    /** Since the support articles are only available in Dutch and English, we manage the
     * locale here to make sure we fetch the right translation and have the right co    ntact
     * form locale. Since there is only Dutch and English support channels right now, we
     * have the default to English when the locale is not Dutch.
     */
    if (locale !== 'nl') {
        localeSupport = 'en-gb';
    }
    dispatch(setSupportLocale(localeSupport));
    return localeSupport;
};

export const fetchInfoArticle = (articleId: string | null) => async (dispatch: any) => {
    dispatch(setIsFetchingInfoArticle(true));
    dispatch(setFetchInfoArticleError(null));
    const localeToFetch = await dispatch(initializeSupportLocale());

    const baseUrl = VALIDSIGN_SUPPORT_API_URL;
    const url = `${baseUrl}/${localeToFetch}/articles/${articleId}.json`;

    try {
        const response = await httpClient.get(url, {
            withCredentials: false
        });

        if (!response.data) {
            throw new Error('Article data is missing');
        }

        dispatch(setInfoArticle(response.data.article));
    } catch (error: unknown) {
        dispatch(setFetchInfoArticleError('esl.http.status_404'));
        dispatch(setInfoArticle(null));
    } finally {
        dispatch(setIsFetchingInfoArticle(false));
    }
};

export const setSelectedInfoArticleIdAndFetchArticle = (articleId: string | null) => async (dispatch: any) => {
    dispatch(setSelectedInfoArticleId(articleId));

    if (articleId) {
        dispatch(fetchInfoArticle(articleId));
    } else {
        dispatch(setInfoArticle(null));
    }
};

export const selectInfoArticle = (state: InfoArticleRootState) => state.infoArticle.infoArticle;
export const selectIsFetchingInfoArticle = (state: InfoArticleRootState) => state.infoArticle.isFetchingInfoArticle;
export const selectFetchInfoArticleError = (state: InfoArticleRootState) => state.infoArticle.fetchInfoArticleError;
export const selectSelectedInfoArticleId = (state: InfoArticleRootState) => state.infoArticle.selectedInfoArticleId;
export const selectSupportLocale = (state: InfoArticleRootState) => state.infoArticle.supportLocale;

export default infoArticleSlice.reducer;
